import { deriveFieldValidations } from "@capture/shared";
import { useFormConfigStore } from "../../state/store";

/**
 * Read-only "Validation" readout (E-022 Slice 6 DO#3).
 *
 * Shows, in the field drawer's Basics tab, exactly which validation rules are
 * DERIVED from this field's settings (Required / Pattern) — the same virtual
 * rules the Rules tab lists under "Derived from fields" and the VS enforces.
 * Read-only: the source is the field's own settings (edited above); a jump
 * link sends the operator to the Rules tab to tune enforcement.
 */
export function ValidationReadout({
  onJumpToRules,
  onAddCustomCheck,
}: {
  onJumpToRules?: () => void;
  /**
   * Lane C Part 2 — author a check the field settings can't derive (a custom
   * pattern/comparison/message). Creates a field-scoped validate rule
   * pre-targeted to this field and jumps to its editor on the Rules tab. The
   * host (FieldEditorDrawer) owns the mutation; this component only surfaces
   * the affordance.
   */
  onAddCustomCheck?: () => void;
}) {
  const content = useFormConfigStore((s) => s.content);
  const selectedFieldName = useFormConfigStore((s) => s.selectedFieldName);
  const field = content?.fields.find((f) => f.name === selectedFieldName) ?? null;

  if (!field) return null;

  const derived = deriveFieldValidations(field);

  return (
    <section className="field-section" aria-label="Derived validation">
      <h3 className="field-section__title">Validation (derived)</h3>
      <p className="field-section__hint">
        Validation rules generated automatically from the settings above. They run during keying — no
        separate rule to author.
      </p>
      {derived.length === 0 ? (
        <p className="field-section__hint" data-testid="validation-readout-empty">
          No validation derived from this field yet. Set <strong>Required</strong> or a{" "}
          <strong>Regex pattern</strong> above to derive one.
        </p>
      ) : (
        <ul className="validation-readout__list">
          {derived.map((d) => (
            <li key={d.source} className="validation-readout__item" data-testid="validation-readout-item">
              <span className="validation-readout__label">{d.sourceLabel}</span>
              {/* E-024 — cosmetic cross-reference label (documentation only). */}
              {d.label && (
                <span className="validation-readout__name" data-testid="validation-readout-label">
                  {d.label}
                </span>
              )}
              {d.rule.type === "pattern" && d.rule.pattern && (
                <code className="validation-readout__pattern">{d.rule.pattern}</code>
              )}
              {/* E-024 — authored message that REPLACES the generated one at keying. */}
              {d.message && (
                <span className="validation-readout__message" data-testid="validation-readout-message">
                  {d.message}
                </span>
              )}
              <span className="validation-readout__sev">{d.severity}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="validation-readout__actions">
        {onAddCustomCheck && (
          <button
            type="button"
            className="validation-readout__add"
            onClick={onAddCustomCheck}
          >
            + Add a custom check
          </button>
        )}
        {onJumpToRules && (
          <button type="button" className="validation-readout__jump" onClick={onJumpToRules}>
            Open in Automations &rarr;
          </button>
        )}
      </div>
    </section>
  );
}
